import { useLayoutContext } from "arkh";
import React, { useMemo } from "react";

import { RoutineOccasionHistoryListItem } from "./RoutineOccasionHistoryListItem";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { RoutineOccasionHistoryScreenList_Routine$key } from "./__generated__/RoutineOccasionHistoryScreenList_Routine.graphql";
import { RoutineOccasionHistoryScreenQuery } from "./__generated__/RoutineOccasionHistoryScreenQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { RoutineOccasionHistoryListComponent } from "./RoutineOccasionHistoryListComponent";
import { RoutineOccasionHistoryModalContainer } from "./RoutineOccassionHistoryContainer";

const RoutinesOccasionsHistoryList = (props: {
  routine: RoutineOccasionHistoryScreenList_Routine$key;
  routineId: string;
  onSelectOccasion: (sequenceNumber: number) => void;
  selectedSequenceNumber: number;
  onClose: () => void;
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment RoutineOccasionHistoryScreenList_Routine on Routine
      @refetchable(queryName: "RoutineOccasionHistoryListRefetchQuery") {
        id
        occasions(first: $first, after: $after, storeId: $storeId)
          @connection(key: "RoutineOccasionHistoryList_occasions") {
          edges {
            node {
              id
              sequenceNumber
              ...RoutineOccasionHistoryListItem_Occasion
            }
          }
        }
      }
    `,
    props.routine
  );

  const layout = useLayoutContext();

  const occasions = useMemo(
    () => data?.occasions?.edges.map((item) => item.node) ?? [],
    [data?.occasions?.edges]
  );

  return (
    <RoutineOccasionHistoryListComponent
      occasions={occasions}
      getItemLayout={(_, index) => ({
        length: 75 + 1,
        offset: (75 + 1) * index + layout.space.medium,
        index,
      })}
      onClose={props.onClose}
      isLoadingNext={isLoadingNext}
      loadNext={loadNext}
      hasNext={hasNext}
      renderItem={({ item, index }) => {
        return (
          <RoutineOccasionHistoryListItem
            isTopItem={index === 0}
            isBottomItem={index === occasions.length - 1}
            onPress={props.onSelectOccasion}
            occasion={item}
            isActive={props.selectedSequenceNumber === item.sequenceNumber}
          />
        );
      }}
    />
  );
};

const RoutinesOccasionsHistoryComponent = (props: {
  routineId: string;
  storeId?: string;
  fetchKey: string;
  onSelectOccasion: (sequenceNumber: number) => void;
  onClose: () => void;
  selectedSequenceNumber: number;
}) => {
  const data = useLazyLoadQuery<RoutineOccasionHistoryScreenQuery>(
    graphql`
      query RoutineOccasionHistoryScreenQuery(
        $routineId: ID!
        $first: Int
        $after: String
        $storeId: ID
      ) {
        routine(id: $routineId) {
          id
          ...RoutineOccasionHistoryScreenList_Routine
        }
      }
    `,
    {
      routineId: props.routineId,
      storeId: props.storeId,
      first: 10,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );

  if (!data.routine) {
    return null;
  }
  return (
    <RoutinesOccasionsHistoryList
      routine={data.routine}
      routineId={props.routineId}
      selectedSequenceNumber={props.selectedSequenceNumber}
      onSelectOccasion={props.onSelectOccasion}
      onClose={props.onClose}
    />
  );
};

function InnerRoutineOccasionHistory(props: {
  routineId: string;
  storeId?: string;
  sequenceNumber: number;
  onSelectOccasion: (sequenceNumber: number) => void;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <RoutineOccasionHistoryModalContainer
      onSelectOccasion={props.onSelectOccasion}
      isOpen={props.isOpen}
      onClose={props.onClose}
    >
      {({ onCloseOccasion, selectSequence }) => (
        <LazyQueryNetworkBoundrary>
          {(fetchKey) => (
            <RoutinesOccasionsHistoryComponent
              fetchKey={fetchKey}
              onClose={onCloseOccasion}
              onSelectOccasion={selectSequence}
              routineId={props.routineId}
              selectedSequenceNumber={props.sequenceNumber}
              storeId={props.storeId}
            />
          )}
        </LazyQueryNetworkBoundrary>
      )}
    </RoutineOccasionHistoryModalContainer>
  );
}

export const RoutineOccasionHistory = React.memo(InnerRoutineOccasionHistory);
